import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

interface User {
  username: string;
  email: string;
  status: string;
  enabled: boolean;
  role: string;
  canExportSms: boolean;
  created: string;
  lastModified: string;
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  CONFIRMED: { label: 'Active', className: 'badge-success' },
  FORCE_CHANGE_PASSWORD: { label: 'Pending', className: 'badge-warning' },
  RESET_REQUIRED: { label: 'Reset Required', className: 'badge-warning' },
  DISABLED: { label: 'Disabled', className: 'badge-danger' },
};

function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'viewer' | 'admin'>('viewer');
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const loadUsers = () => {
    setLoading(true);
    setLoadError('');
    api.listUsers()
      .then((data: any) => setUsers(data.users || []))
      .catch((err: any) => {
        console.error('Load users error:', err);
        setLoadError(err.message || 'Failed to load users');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 8000);
  };

  const handleAddUser = async () => {
    const email = newEmail.trim();
    if (!email) {
      showMessage('Please enter an email address', 'error');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showMessage('Please enter a valid email address', 'error');
      return;
    }

    setAdding(true);
    try {
      const result = await api.createUser(email, newRole);
      showMessage(result.message, 'success');
      setNewEmail('');
      loadUsers();
    } catch (err: any) {
      console.error('Create user error:', err);
      showMessage(err.message || 'Failed to create user', 'error');
    }
    setAdding(false);
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Remove ${user.email}? They will lose access immediately.`)) return;

    try {
      await api.deleteUser(user.username);
      showMessage(`${user.email} removed`, 'success');
      loadUsers();
    } catch (err: any) {
      showMessage(err.message || 'Failed to remove user', 'error');
    }
  };

  const handleResetPassword = async (user: User) => {
    if (!window.confirm(`Send a new temporary password to ${user.email}?`)) return;

    try {
      const result = await api.resetUserPassword(user.username);
      showMessage(result.message, 'success');
      loadUsers();
    } catch (err: any) {
      showMessage(err.message || 'Failed to reset password', 'error');
    }
  };

  const handleToggleRole = async (user: User) => {
    const newRole = user.role === 'admin' ? 'viewer' : 'admin';
    if (!window.confirm(`Change ${user.email} from ${user.role} to ${newRole}?`)) return;

    try {
      await api.changeUserRole(user.username, newRole);
      showMessage(`${user.email} is now ${newRole}`, 'success');
      loadUsers();
    } catch (err: any) {
      showMessage(err.message || 'Failed to change role', 'error');
    }
  };

  const handleToggleSmsExport = async (user: User) => {
    const next = !user.canExportSms;
    setUsers(prev => prev.map(u => u.username === user.username ? { ...u, canExportSms: next } : u));
    try {
      await api.setUserCapability(user.username, 'sms_export', next);
      showMessage(`${user.email}: SMS Export ${next ? 'granted' : 'revoked'}`, 'success');
    } catch (err: any) {
      setUsers(prev => prev.map(u => u.username === user.username ? { ...u, canExportSms: !next } : u));
      showMessage(err.message || 'Failed to update capability', 'error');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>User Access</h1>
        <p>Manage who can access Pulse Storage</p>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          borderRadius: '6px',
          background: message.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: message.type === 'success' ? '#2e7d32' : '#c62828',
          border: `1px solid ${message.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`,
        }}>
          {message.text}
        </div>
      )}

      <div style={{
        background: '#fff',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '12px' }}>Add User</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="email@example.com"
            onKeyDown={e => { if (e.key === 'Enter') handleAddUser(); }}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '14px',
            }}
          />
          <select
            value={newRole}
            onChange={e => setNewRole(e.target.value as 'viewer' | 'admin')}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '14px',
              background: '#fff',
            }}
          >
            <option value="viewer">Viewer</option>
            <option value="admin">Admin</option>
          </select>
          <button
            type="button"
            onClick={handleAddUser}
            disabled={adding}
            style={{
              padding: '8px 20px',
              borderRadius: '6px',
              border: 'none',
              background: adding ? '#999' : '#00bcd4',
              color: '#fff',
              fontSize: '14px',
              cursor: adding ? 'wait' : 'pointer',
            }}
          >
            {adding ? 'Adding...' : 'Add User'}
          </button>
        </div>
        <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#888' }}>
          <strong>Admin:</strong> Full access — manage users, access control, SFTP setup.{' '}
          <strong>Viewer:</strong> Listen to calls, transcribe, download.
        </p>
      </div>

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : loadError ? (
        <div style={{
          padding: '16px',
          background: '#ffebee',
          borderRadius: '8px',
          color: '#c62828',
          marginBottom: '16px',
        }}>
          Error loading users: {loadError}
          <button onClick={loadUsers} style={{ marginLeft: '12px', cursor: 'pointer' }}>Retry</button>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>SMS Export</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>No users found</td></tr>
            ) : users.map(user => {
              const statusInfo = STATUS_LABELS[user.status] || { label: user.status, className: '' };
              return (
                <tr key={user.username}>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge ${user.role === 'admin' ? 'badge-success' : 'badge-warning'}`}>
                      {user.role === 'admin' ? 'Admin' : 'Viewer'}
                    </span>
                  </td>
                  <td>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: user.role === 'admin' ? 'default' : 'pointer', opacity: user.role === 'admin' ? 0.6 : 1 }}
                      title={user.role === 'admin' ? 'Admins always have SMS Export access' : ''}>
                      <input
                        type="checkbox"
                        checked={user.role === 'admin' || user.canExportSms}
                        disabled={user.role === 'admin'}
                        onChange={() => handleToggleSmsExport(user)}
                      />
                    </label>
                  </td>
                  <td>
                    <span className={`badge ${statusInfo.className}`}>{statusInfo.label}</span>
                  </td>
                  <td>{user.created ? new Date(user.created).toLocaleDateString() : '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => handleToggleRole(user)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '4px',
                          border: '1px solid #80deea',
                          background: '#e0f7fa',
                          color: '#0097a7',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        {user.role === 'admin' ? 'Make Viewer' : 'Make Admin'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleResetPassword(user)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          background: '#fff',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        Reset Password
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(user)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '4px',
                          border: '1px solid #ef9a9a',
                          background: '#fff',
                          color: '#c62828',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default Users;
